import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Menu, X } from 'lucide-react';
import { Logo } from './Logo';
import { usePlausible } from '../hooks/usePlausible';

interface PublicHeaderProps {
  fixed?: boolean;
}

const NAV_LINKS = [
  { to: '/about', label: 'About Us' },
  { to: '/security', label: 'Security' },
  { to: '/team', label: 'Team' },
  { to: '/jobs', label: 'Careers' },
  { to: '/blog', label: 'Blog' },
  { to: '/contact', label: 'Contact' },
];

export const PublicHeader: React.FC<PublicHeaderProps> = ({ fixed = false }) => {
  usePlausible();
  const scroll = () => window.scrollTo(0, 0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const closeMobileMenu = useCallback(() => setMobileMenuOpen(false), []);

  useEffect(() => {
    if (!mobileMenuOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        closeMobileMenu();
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMobileMenu();
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [mobileMenuOpen, closeMobileMenu]);

  return (
    <header
      ref={menuRef}
      className={`bg-white/80 backdrop-blur-xl border-b border-medical-200/60 shadow-sm ${
        fixed ? 'fixed top-0 left-0 right-0 z-50 transition-all duration-300' : ''
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" onClick={scroll}>
            <Logo size="lg" />
          </Link>
          <nav className="hidden md:flex items-center space-x-1">
            {NAV_LINKS.map(({ to, label }) => (
              <Link key={to} to={to} onClick={scroll} className="text-medical-600 hover:text-medical-900 hover:bg-medical-50 font-medium text-[13px] tracking-wide uppercase px-3 py-1.5 rounded-md transition-all duration-200">{label}</Link>
            ))}
            <Link to="/demo" onClick={scroll} className="text-medical-600 hover:text-medical-900 hover:bg-medical-50 font-medium text-[13px] tracking-wide uppercase px-3 py-1.5 rounded-md transition-all duration-200">Demo</Link>
          </nav>
          <div className="hidden lg:flex items-center space-x-4">
            <Link
              to="/signin"
              className="border border-medical-300 text-medical-700 hover:bg-medical-50 hover:border-medical-400 font-semibold text-sm px-4 py-2 rounded-lg transition-all"
            >
              Sign In
            </Link>
            <Link
              to="/signup"
              className="btn-primary group flex items-center space-x-2 px-6 py-3 text-base shadow-lg shadow-primary-200/50 hover:shadow-xl hover:shadow-primary-300/50"
            >
              <span>Get Started</span>
              <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
          <button
            className="md:hidden p-2 rounded-lg text-medical-600 hover:text-medical-900 hover:bg-medical-100 transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile menu dropdown */}
        <div
          className={`md:hidden overflow-hidden transition-all duration-300 ease-in-out ${
            mobileMenuOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <nav className="pb-4 pt-2 space-y-1 border-t border-medical-200/60">
            {NAV_LINKS.map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                onClick={() => { scroll(); closeMobileMenu(); }}
                className="block px-3 py-2.5 rounded-lg text-medical-600 hover:text-medical-900 hover:bg-medical-50 font-medium text-base transition-colors"
              >
                {label}
              </Link>
            ))}
            <div className="pt-3 px-3">
              <Link
                to="/demo"
                onClick={() => { scroll(); closeMobileMenu(); }}
                className="btn-primary flex items-center justify-center space-x-2 w-full py-3 text-base rounded-lg shadow-lg shadow-primary-200/50"
              >
                <span>Watch the Demo</span>
                <ArrowRight className="h-5 w-5" />
              </Link>
            </div>
          </nav>
        </div>
      </div>
    </header>
  );
};
